import { useEffect, useState } from 'react';
import { api } from '../../utils/apiClient';

// Admin screen to connect a restaurant's cash system (POS). Credentials are
// stored per-restaurant in the DB (encrypted), so each restaurant self-onboards
// here instead of editing env. Currently supports Simphony.

interface PosConnectionPanelProps { restaurantId: string }

const FIELDS: { key: string; label: string; placeholder?: string }[] = [
  { key: 'appHost', label: 'Application server (API)', placeholder: 'https://...-sts.oraclemicros.com' },
  { key: 'authHost', label: 'Authentication server', placeholder: 'https://orsidm...oraclerestaurants.com' },
  { key: 'clientId', label: 'Client ID' },
  { key: 'username', label: 'Имя аккаунта' },
  { key: 'orgShortName', label: 'Organization Short Name' },
  { key: 'locRef', label: 'Location ref (точка)' },
  { key: 'rvcRef', label: 'Revenue Center ref' },
  { key: 'cashierPin', label: 'PIN кассира' },
  { key: 'tenderId', label: 'Tender ID', placeholder: '100' },
  { key: 'orderTypeRef', label: 'Order type ref', placeholder: '1' },
];

export default function PosConnectionPanel({ restaurantId }: PosConnectionPanelProps) {
  const [config, setConfig] = useState<Record<string, string>>({});
  const [password, setPassword] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [hasExisting, setHasExisting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true); setMsg(''); setErr('');
    api.get<{ connection: { config?: Record<string, unknown>; enabled: boolean } | null }>(`/api/pos/connection/${restaurantId}`)
      .then(({ data }) => {
        const c = data.connection;
        if (c) {
          setHasExisting(true);
          setEnabled(c.enabled);
          const cfg: Record<string, string> = {};
          Object.entries(c.config || {}).forEach(([k, v]) => { cfg[k] = String(v ?? ''); });
          setConfig(cfg);
        } else {
          setHasExisting(false); setConfig({}); setEnabled(true);
        }
      })
      .catch(() => setErr('Не удалось загрузить подключение'))
      .finally(() => setLoading(false));
  }, [restaurantId]);

  const set = (k: string, v: string) => setConfig((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg(''); setErr('');
    try {
      const body: any = { provider: 'simphony', enabled, config };
      if (password) body.password = password;
      await api.post(`/api/pos/connection/${restaurantId}`, body);
      setHasExisting(true); setPassword('');
      setMsg('Сохранено ✓');
    } catch (e: any) {
      setErr(e.response?.data?.error || 'Ошибка сохранения');
    } finally { setSaving(false); }
  };

  if (!restaurantId) return <div className="analytics-section">Выберите ресторан.</div>;

  return (
    <div className="analytics-section" style={{ maxWidth: 620 }}>
      <h3 className="section-title">Подключение кассы (Simphony)</h3>
      <p style={{ opacity: 0.7, fontSize: 14, marginTop: -8 }}>
        Доступы берутся с экрана «Connection Details» в Simphony. Пароль хранится зашифрованным.
        {hasExisting ? ' Подключение уже настроено.' : ' Подключение ещё не настроено.'}
      </p>

      {loading ? <div>Загрузка…</div> : (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0 16px' }}>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span>Включено</span>
          </label>

          {FIELDS.map((f) => (
            <div key={f.key} style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{f.label}</label>
              <input
                type="text"
                value={config[f.key] ?? ''}
                placeholder={f.placeholder}
                onChange={(e) => set(f.key, e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 10, border: '1px solid var(--admin-border-soft)' }}
              />
            </div>
          ))}

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Пароль аккаунта</label>
            <input
              type="password"
              value={password}
              placeholder={hasExisting ? 'оставьте пустым, чтобы не менять' : 'пароль API-аккаунта'}
              onChange={(e) => setPassword(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 10, border: '1px solid var(--admin-border-soft)' }}
            />
          </div>

          {msg && <div style={{ color: '#2a6', marginBottom: 8 }}>{msg}</div>}
          {err && <div style={{ color: '#b23', marginBottom: 8 }}>{err}</div>}

          <button
            onClick={save}
            disabled={saving}
            style={{ padding: '12px 18px', borderRadius: 12, border: 'none', background: '#1f1b16', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
          >
            {saving ? 'Сохраняем…' : 'Сохранить подключение'}
          </button>
        </>
      )}
    </div>
  );
}
